const initialState = {
  visible: false,
  featureEnabled: false,
  loadingConversations: false,
  loadingMessages: false,
  sendingMessage: false,
  conversations: [],
  messages: [],
  selectedConversationSlug: null,
  // Internal bookkeeping: tracks which message IDs in `messages` are client-side
  // placeholders (optimistic-user-* / optimistic-assistant-*). Used by the reducer
  // across send, retry, stream, and failure branches to know which messages to
  // swap out or remove. Not read by any component — only consumed within this reducer.
  optimisticMessageIds: [],
  retryingMessageIds: [],
  isNearBottom: true,
  hasUnseenNewMessages: false,
  error: null,
  windowPosition: null,
  windowSize: { width: 760, height: 600 },
  streamingResponse: false,
  sidebarCollapsed: false,
  viewerChatVisible: false,
};

const normalizeMessageId = (messageId) => String(messageId);
const isOptimisticAssistantId = (messageId) => normalizeMessageId(messageId).startsWith('optimistic-assistant-');
const isOptimisticUserId = (messageId) => normalizeMessageId(messageId).startsWith('optimistic-user-');
const isTerminalStatus = (status) => status === 'completed' || status === 'failed';

const removeMessagesByIds = (messages, messageIds) => {
  if (!messageIds || messageIds.length === 0) return messages;
  const idSet = new Set(messageIds.map((id) => normalizeMessageId(id)));
  return messages.filter((messageItem) => !idSet.has(normalizeMessageId(messageItem.id)));
};

const appendIfMissing = (messages, messageToAppend) => {
  if (!messageToAppend) return messages;
  if (messages.some((messageItem) => normalizeMessageId(messageItem.id) === normalizeMessageId(messageToAppend.id))) {
    return messages;
  }

  return [...messages, messageToAppend];
};

const upsertMessage = (messages, messageToUpsert) => {
  if (!messageToUpsert) return messages;

  const existingIndex = messages.findIndex(
    (messageItem) => normalizeMessageId(messageItem.id) === normalizeMessageId(messageToUpsert.id),
  );

  if (existingIndex === -1) {
    return [...messages, messageToUpsert];
  }

  const next = [...messages];
  next[existingIndex] = {
    ...next[existingIndex],
    ...messageToUpsert,
  };
  return next;
};

const removeOptimisticAssistantMessages = (messages) => (
  messages.filter((messageItem) => !isOptimisticAssistantId(messageItem.id))
);

const stripOptimisticAssistantIds = (messageIds) => (
  messageIds.filter((messageId) => !isOptimisticAssistantId(messageId))
);

const upsertConversation = (conversations, updatedConversation) => {
  const existingIndex = conversations.findIndex(
    (conversation) => conversation.slug === updatedConversation.slug,
  );

  if (existingIndex === -1) {
    return [updatedConversation, ...conversations];
  }

  const next = [...conversations];
  next[existingIndex] = updatedConversation;
  return next;
};

const sortConversations = (conversations) => (
  [...conversations].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
);

const markUnseenIfScrolledUp = (state, didAppendMessages) => (
  didAppendMessages && !state.isNearBottom ? true : state.hasUnseenNewMessages
);

const hasPendingAssistantMessage = (messages = []) => (
  messages.some(
    (messageItem) => messageItem?.role === 'assistant' && messageItem?.status === 'pending',
  )
);

const getMessageById = (messages = [], messageId) => (
  messages.find((messageItem) => normalizeMessageId(messageItem.id) === normalizeMessageId(messageId))
);

const mergeFetchedWithLocalStreamingMessages = (localMessages = [], fetchedMessages = []) => {
  const fetchedIds = new Set(fetchedMessages.map((messageItem) => normalizeMessageId(messageItem.id)));

  const mergedMessages = fetchedMessages.map((fetchedMessage) => {
    const localMessage = getMessageById(localMessages, fetchedMessage.id);
    if (!localMessage || localMessage.role !== 'assistant') return fetchedMessage;

    if (localMessage.status === 'pending' && !isTerminalStatus(fetchedMessage.status)) {
      return localMessage;
    }

    if (isTerminalStatus(localMessage.status) && !isTerminalStatus(fetchedMessage.status)) {
      return localMessage;
    }

    return fetchedMessage;
  });

  const missingLocalStreamingMessages = localMessages.filter((localMessage) => (
    localMessage?.role === 'assistant'
    && localMessage?.status === 'pending'
    && !isOptimisticAssistantId(localMessage.id)
    && !fetchedIds.has(normalizeMessageId(localMessage.id))
  ));

  return [...mergedMessages, ...missingLocalStreamingMessages];
};

const aiChat = (state = initialState, action = {}) => {
  switch (action.type) {
    case 'AI_CHAT_SET_VISIBLE':
      return {
        ...state,
        visible: action.payload.visible,
        // Close the viewer chat when the floating modal opens so they
        // don't fight over the shared selectedConversationSlug/messages.
        viewerChatVisible: action.payload.visible ? false : state.viewerChatVisible,
      };
    case 'AI_CHAT_SET_FEATURE_ENABLED':
      return {
        ...state,
        featureEnabled: action.payload.enabled,
      };
    case 'AI_CHAT_SELECT_CONVERSATION':
      if (action.payload.slug === state.selectedConversationSlug) {
        return state;
      }

      return {
        ...state,
        selectedConversationSlug: action.payload.slug,
        messages: [],
        optimisticMessageIds: [],
        streamingResponse: false,
        hasUnseenNewMessages: false,
        isNearBottom: true,
      };
    case 'AI_CHAT_SET_NEAR_BOTTOM':
      return {
        ...state,
        isNearBottom: action.payload.isNearBottom,
        hasUnseenNewMessages: action.payload.isNearBottom ? false : state.hasUnseenNewMessages,
      };
    case 'AI_CHAT_CLEAR_UNSEEN_MESSAGES':
      return {
        ...state,
        hasUnseenNewMessages: false,
      };
    case 'BEGIN_GET_AI_CHAT_CONVERSATIONS':
      return {
        ...state,
        loadingConversations: true,
        error: null,
      };
    case 'FINISH_GET_AI_CHAT_CONVERSATIONS':
    {
      const conversations = action.payload.data.conversations || [];
      const hasSelectedConversation = conversations.some(
        (conversation) => conversation.slug === state.selectedConversationSlug,
      );
      const selectedConversationSlug = hasSelectedConversation
        ? state.selectedConversationSlug
        : (conversations[0]?.slug || null);

      return {
        ...state,
        loadingConversations: false,
        conversations,
        selectedConversationSlug,
        messages: selectedConversationSlug === state.selectedConversationSlug ? state.messages : [],
        optimisticMessageIds: selectedConversationSlug === state.selectedConversationSlug ? state.optimisticMessageIds : [],
      };
    }
    case 'FAILED_GET_AI_CHAT_CONVERSATIONS':
      return {
        ...state,
        loadingConversations: false,
        error: action.payload.error,
      };
    case 'BEGIN_CREATE_AI_CHAT_CONVERSATION':
      return {
        ...state,
        loadingConversations: true,
        error: null,
      };
    case 'FINISH_CREATE_AI_CHAT_CONVERSATION': {
      const createdConversation = action.payload.data.conversation;
      return {
        ...state,
        loadingConversations: false,
        conversations: sortConversations(upsertConversation(state.conversations, createdConversation)),
        selectedConversationSlug: createdConversation.slug,
        messages: [],
        optimisticMessageIds: [],
        hasUnseenNewMessages: false,
        isNearBottom: true,
      };
    }
    case 'FAILED_CREATE_AI_CHAT_CONVERSATION':
      return {
        ...state,
        loadingConversations: false,
        error: action.payload.error,
      };
    case 'BEGIN_CREATE_AI_CHAT_DOCUMENT_CONVERSATION':
      return {
        ...state,
        loadingConversations: true,
        error: null,
      };
    case 'FINISH_CREATE_AI_CHAT_DOCUMENT_CONVERSATION': {
      const docConversation = action.payload.data.conversation;
      const shouldPreserveMessages = action.payload.data.existing
        && docConversation.slug === state.selectedConversationSlug;
      return {
        ...state,
        loadingConversations: false,
        conversations: sortConversations(upsertConversation(state.conversations, docConversation)),
        selectedConversationSlug: docConversation.slug,
        messages: shouldPreserveMessages ? state.messages : [],
        optimisticMessageIds: shouldPreserveMessages ? state.optimisticMessageIds : [],
        streamingResponse: shouldPreserveMessages ? state.streamingResponse : false,
        hasUnseenNewMessages: false,
        isNearBottom: true,
      };
    }
    case 'FAILED_CREATE_AI_CHAT_DOCUMENT_CONVERSATION':
      return {
        ...state,
        loadingConversations: false,
        error: action.payload.error,
      };
    case 'BEGIN_RENAME_AI_CHAT_CONVERSATION':
      return {
        ...state,
        error: null,
      };
    case 'FINISH_RENAME_AI_CHAT_CONVERSATION': {
      const { conversation } = action.payload.data;
      return {
        ...state,
        conversations: sortConversations(upsertConversation(state.conversations, conversation)),
      };
    }
    case 'FAILED_RENAME_AI_CHAT_CONVERSATION':
      return {
        ...state,
        error: action.payload.error,
      };
    case 'BEGIN_GET_AI_CHAT_MESSAGES':
      return {
        ...state,
        loadingMessages: true,
        error: null,
      };
    case 'FINISH_GET_AI_CHAT_MESSAGES': {
      if (action.payload.conversationSlug !== state.selectedConversationSlug) {
        return {
          ...state,
          loadingMessages: false,
        };
      }

      const fetchedMessages = action.payload.data.messages || [];
      const shouldPreserveLocalStreaming = hasPendingAssistantMessage(state.messages);
      const mergedMessages = shouldPreserveLocalStreaming
        ? mergeFetchedWithLocalStreamingMessages(state.messages, fetchedMessages)
        : fetchedMessages;

      return {
        ...state,
        loadingMessages: false,
        streamingResponse: hasPendingAssistantMessage(mergedMessages),
        messages: mergedMessages,
        optimisticMessageIds: [],
        hasUnseenNewMessages: false,
      };
    }
    case 'FAILED_GET_AI_CHAT_MESSAGES':
      if (action.payload.conversationSlug !== state.selectedConversationSlug) {
        return {
          ...state,
          loadingMessages: false,
        };
      }

      return {
        ...state,
        loadingMessages: false,
        error: action.payload.error,
      };
    case 'BEGIN_SEND_AI_CHAT_MESSAGE': {
      const {
        conversationSlug,
        optimisticUserMessage,
        optimisticAssistantMessage,
        optimisticIds,
      } = action.payload;
      const shouldAppend = conversationSlug === state.selectedConversationSlug;
      const nextMessages = shouldAppend
        ? [...state.messages, optimisticUserMessage, optimisticAssistantMessage]
        : state.messages;

      return {
        ...state,
        sendingMessage: true,
        error: null,
        messages: nextMessages,
        optimisticMessageIds: [...state.optimisticMessageIds, ...optimisticIds],
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, shouldAppend),
      };
    }
    case 'FINISH_SEND_AI_CHAT_MESSAGE': {
      const { user_message: userMessage } = action.payload.data;
      const { conversationSlug, optimisticIds, stream } = action.payload;
      const streamAssistantId = stream?.assistant_message_id;
      const shouldAppend = conversationSlug === state.selectedConversationSlug;

      if (!shouldAppend) {
        return {
          ...state,
          sendingMessage: false,
        };
      }

      const optimisticUserIds = optimisticIds.filter((id) => isOptimisticUserId(id));
      const optimisticAssistantIds = optimisticIds.filter((id) => isOptimisticAssistantId(id));
      const messagesWithoutOptimisticUser = removeMessagesByIds(state.messages, optimisticUserIds);
      const optimisticAssistantMessages = messagesWithoutOptimisticUser.filter(
        (messageItem) => optimisticAssistantIds.map((id) => normalizeMessageId(id)).includes(normalizeMessageId(messageItem.id)),
      );
      const baseMessages = removeMessagesByIds(
        messagesWithoutOptimisticUser,
        optimisticAssistantIds,
      );
      let messagesWithServerPayload = appendIfMissing(baseMessages, userMessage);
      optimisticAssistantMessages.forEach((messageItem) => {
        messagesWithServerPayload = appendIfMissing(messagesWithServerPayload, messageItem);
      });

      let optimisticMessageIds = state.optimisticMessageIds.filter(
        (messageId) => !optimisticUserIds.map((id) => normalizeMessageId(id)).includes(normalizeMessageId(messageId)),
      );

      if (streamAssistantId) {
        messagesWithServerPayload = removeOptimisticAssistantMessages(messagesWithServerPayload);
        const existingStreamMessage = getMessageById(messagesWithServerPayload, streamAssistantId);
        const canUpsertPendingStub = !isTerminalStatus(existingStreamMessage?.status);
        if (canUpsertPendingStub) {
          messagesWithServerPayload = upsertMessage(messagesWithServerPayload, {
            id: streamAssistantId,
            role: 'assistant',
            status: 'pending',
            content: '',
            created_at: new Date().toISOString(),
            attachments: [],
          });
        }
        optimisticMessageIds = stripOptimisticAssistantIds(optimisticMessageIds);
      } else {
        optimisticMessageIds = [
          ...optimisticMessageIds,
          ...optimisticAssistantIds.filter(
            (id) => !optimisticMessageIds.map((existingId) => normalizeMessageId(existingId)).includes(normalizeMessageId(id)),
          ),
        ];
      }

      return {
        ...state,
        sendingMessage: false,
        streamingResponse: hasPendingAssistantMessage(messagesWithServerPayload),
        messages: messagesWithServerPayload,
        optimisticMessageIds,
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, shouldAppend),
      };
    }
    case 'FAILED_SEND_AI_CHAT_MESSAGE': {
      const { conversationSlug, optimisticIds } = action.payload;

      if (conversationSlug !== state.selectedConversationSlug) {
        return {
          ...state,
          sendingMessage: false,
        };
      }

      return {
        ...state,
        sendingMessage: false,
        messages: removeMessagesByIds(state.messages, optimisticIds),
        optimisticMessageIds: state.optimisticMessageIds.filter(
          (messageId) => !optimisticIds.map((id) => normalizeMessageId(id)).includes(normalizeMessageId(messageId)),
        ),
        error: action.payload.error,
      };
    }
    case 'BEGIN_RETRY_AI_CHAT_MESSAGE':
    {
      const {
        conversationSlug,
        failedMessageId,
        optimisticAssistantMessage,
        optimisticAssistantId,
      } = action.payload;
      const shouldAppend = conversationSlug === state.selectedConversationSlug;

      return {
        ...state,
        error: null,
        retryingMessageIds: [...state.retryingMessageIds, failedMessageId],
        messages: shouldAppend
          ? appendIfMissing(state.messages, optimisticAssistantMessage)
          : state.messages,
        optimisticMessageIds: shouldAppend
          ? [...state.optimisticMessageIds, optimisticAssistantId]
          : state.optimisticMessageIds,
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, shouldAppend),
      };
    }
    case 'FINISH_RETRY_AI_CHAT_MESSAGE': {
      const {
        conversationSlug,
        failedMessageId,
        optimisticAssistantId,
        stream,
      } = action.payload;
      const streamAssistantId = stream?.assistant_message_id;
      const shouldAppend = conversationSlug === state.selectedConversationSlug;

      if (!shouldAppend) {
        return {
          ...state,
          retryingMessageIds: state.retryingMessageIds.filter(
            (id) => normalizeMessageId(id) !== normalizeMessageId(failedMessageId),
          ),
        };
      }

      let nextMessages = state.messages;
      let optimisticMessageIds = state.optimisticMessageIds;

      if (streamAssistantId) {
        nextMessages = removeMessagesByIds(state.messages, [optimisticAssistantId]);
        nextMessages = removeOptimisticAssistantMessages(nextMessages);
        const existingStreamMessage = getMessageById(nextMessages, streamAssistantId);
        const canUpsertPendingStub = !isTerminalStatus(existingStreamMessage?.status);
        if (canUpsertPendingStub) {
          nextMessages = upsertMessage(nextMessages, {
            id: streamAssistantId,
            role: 'assistant',
            status: 'pending',
            content: '',
            created_at: new Date().toISOString(),
            attachments: [],
          });
        }
        optimisticMessageIds = stripOptimisticAssistantIds(optimisticMessageIds);
      }

      return {
        ...state,
        streamingResponse: hasPendingAssistantMessage(nextMessages),
        messages: nextMessages,
        optimisticMessageIds,
        retryingMessageIds: state.retryingMessageIds.filter(
          (id) => normalizeMessageId(id) !== normalizeMessageId(failedMessageId),
        ),
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, shouldAppend),
      };
    }
    case 'FAILED_RETRY_AI_CHAT_MESSAGE': {
      const {
        conversationSlug,
        failedMessageId,
        optimisticAssistantId,
      } = action.payload;
      const nextRetryingMessageIds = state.retryingMessageIds.filter(
        (id) => normalizeMessageId(id) !== normalizeMessageId(failedMessageId),
      );

      if (conversationSlug !== state.selectedConversationSlug) {
        return {
          ...state,
          retryingMessageIds: nextRetryingMessageIds,
        };
      }

      return {
        ...state,
        messages: removeMessagesByIds(state.messages, [optimisticAssistantId]),
        optimisticMessageIds: state.optimisticMessageIds.filter(
          (id) => normalizeMessageId(id) !== normalizeMessageId(optimisticAssistantId),
        ),
        retryingMessageIds: nextRetryingMessageIds,
        error: action.payload.error,
      };
    }
    case 'AI_CHAT_STREAM_RESET': {
      const {
        conversationSlug,
        assistantMessageId,
      } = action.payload;

      if (conversationSlug !== state.selectedConversationSlug || !assistantMessageId) {
        return state;
      }

      const messageIndex = state.messages.findIndex(
        (messageItem) => normalizeMessageId(messageItem.id) === normalizeMessageId(assistantMessageId),
      );

      if (messageIndex >= 0) {
        const nextMessages = [...state.messages];
        const existingMessage = nextMessages[messageIndex];
        nextMessages[messageIndex] = {
          ...existingMessage,
          status: 'pending',
          content: '',
          grounding: null,
        };

        return {
          ...state,
          streamingResponse: true,
          messages: nextMessages,
          optimisticMessageIds: stripOptimisticAssistantIds(state.optimisticMessageIds),
        };
      }

      const nextMessages = removeOptimisticAssistantMessages(state.messages);
      const mergedMessages = upsertMessage(nextMessages, {
        id: assistantMessageId,
        role: 'assistant',
        status: 'pending',
        content: '',
        created_at: new Date().toISOString(),
        attachments: [],
      });

      return {
        ...state,
        streamingResponse: true,
        messages: mergedMessages,
        optimisticMessageIds: stripOptimisticAssistantIds(state.optimisticMessageIds),
      };
    }
    case 'AI_CHAT_STREAM_DELTA': {
      const {
        conversationSlug,
        assistantMessageId,
        delta,
      } = action.payload;

      if (conversationSlug !== state.selectedConversationSlug || !assistantMessageId) {
        return state;
      }

      const messageIndex = state.messages.findIndex(
        (messageItem) => normalizeMessageId(messageItem.id) === normalizeMessageId(assistantMessageId),
      );

      if (messageIndex >= 0) {
        const nextMessages = [...state.messages];
        const existingMessage = nextMessages[messageIndex];
        if (isTerminalStatus(existingMessage.status)) {
          return state;
        }
        nextMessages[messageIndex] = {
          ...existingMessage,
          status: 'pending',
          content: `${existingMessage.content || ''}${delta || ''}`,
        };

        return {
          ...state,
          messages: nextMessages,
          hasUnseenNewMessages: markUnseenIfScrolledUp(state, true),
        };
      }

      const nextMessages = removeOptimisticAssistantMessages(state.messages);
      const mergedMessages = upsertMessage(nextMessages, {
        id: assistantMessageId,
        role: 'assistant',
        status: 'pending',
        content: delta || '',
        created_at: new Date().toISOString(),
        attachments: [],
      });

      return {
        ...state,
        messages: mergedMessages,
        optimisticMessageIds: stripOptimisticAssistantIds(state.optimisticMessageIds),
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, true),
      };
    }
    case 'AI_CHAT_STREAM_COMPLETED': {
      const {
        conversationSlug,
        assistantMessage,
      } = action.payload;

      if (conversationSlug !== state.selectedConversationSlug || !assistantMessage) {
        return state;
      }

      const nextMessages = removeOptimisticAssistantMessages(state.messages);
      return {
        ...state,
        streamingResponse: false,
        messages: upsertMessage(nextMessages, assistantMessage),
        optimisticMessageIds: stripOptimisticAssistantIds(state.optimisticMessageIds),
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, true),
        error: null,
      };
    }
    case 'AI_CHAT_STREAM_FAILED': {
      const {
        conversationSlug,
        assistantMessage,
        error,
      } = action.payload;

      if (conversationSlug !== state.selectedConversationSlug || !assistantMessage) {
        return state;
      }

      const nextMessages = removeOptimisticAssistantMessages(state.messages);
      return {
        ...state,
        streamingResponse: false,
        messages: upsertMessage(nextMessages, assistantMessage),
        optimisticMessageIds: stripOptimisticAssistantIds(state.optimisticMessageIds),
        hasUnseenNewMessages: markUnseenIfScrolledUp(state, true),
        error: error || state.error,
      };
    }
    case 'BEGIN_DELETE_AI_CHAT_CONVERSATION':
      return {
        ...state,
        loadingConversations: true,
        error: null,
      };
    case 'FINISH_DELETE_AI_CHAT_CONVERSATION': {
      const remainingConversations = state.conversations.filter(
        (conversation) => conversation.slug !== action.payload.slug,
      );
      return {
        ...state,
        loadingConversations: false,
        conversations: remainingConversations,
        selectedConversationSlug: state.selectedConversationSlug === action.payload.slug
          ? (remainingConversations[0]?.slug || null)
          : state.selectedConversationSlug,
        messages: state.selectedConversationSlug === action.payload.slug ? [] : state.messages,
        optimisticMessageIds: state.selectedConversationSlug === action.payload.slug ? [] : state.optimisticMessageIds,
      };
    }
    case 'FAILED_DELETE_AI_CHAT_CONVERSATION':
      return {
        ...state,
        loadingConversations: false,
        error: action.payload.error,
      };
    case 'AI_CHAT_SET_WINDOW_GEOMETRY': {
      const next = { ...state };
      if (action.payload.position !== undefined) next.windowPosition = action.payload.position;
      if (action.payload.size !== undefined) next.windowSize = action.payload.size;
      return next;
    }
    case 'AI_CHAT_SET_SIDEBAR_COLLAPSED':
      return {
        ...state,
        sidebarCollapsed: action.payload.collapsed,
      };
    case 'AI_CHAT_SET_VIEWER_CHAT_VISIBLE': {
      const viewerChatVisible = action.payload.visible;
      return {
        ...state,
        viewerChatVisible,
        // Close the floating modal when the viewer chat opens so they
        // don't fight over the shared selectedConversationSlug/messages.
        visible: viewerChatVisible ? false : state.visible,
        // Inline and floating chat currently share scroll state; reset on
        // inline close so the floating chat resumes auto-scroll behavior.
        isNearBottom: viewerChatVisible ? state.isNearBottom : true,
        hasUnseenNewMessages: viewerChatVisible ? state.hasUnseenNewMessages : false,
      };
    }
    case 'LOGGED_OUT':
    case 'INVALID':
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

export default aiChat;
